import React, { useState, useEffect } from 'react';
import { FileDown, Building2, User, Briefcase, Calendar, ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle, IndianRupee } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching Form-T.xlsx template placeholders exactly (46 total!)
const mockFormData = {
  // Establishment fields
  est_name: "ABC Manufacturing Pvt Ltd",
  est_address: "123 Industrial Area, Whitefield, Bangalore - 560066",

  // Contractor fields
  contractor_name: "XYZ Contractors Pvt Ltd",
  contractor_address: "45 MG Road, Bangalore - 560001",

  // Period
  cur_month: "January",
  cur_year: "2025",

  // Employee array (Form T is a comprehensive wage register)
  employees: [
    {
      slno: "1",
      emp_id: "EMP001",
      emp_name: "Rajesh",
      emp_lname: "Kumar",
      emp_gender: "Male",
      emp_desig: "Machine Operator",
      emp_doj: "2024-06-15",
      emp_uan: "100123456789",
      emp_esino: "1234567890",

      // Attendance & Days
      emp_pay_days: "26",
      emp_ot_hrs: "8",
      nfh: "4", // National/Festival Holidays

      // Earnings
      emp_basic: "20000",
      emp_da: "3000",
      emp_hra: "8000",
      emp_conv: "1600",
      emp_m_allow: "1000", // Medical Allowance
      emp_s_allow: "800", // Special Allowance
      emp_other: "600",
      emp_ot: "2000", // OT wages
      emp_bonus: "1000",
      emp_subsis: "500", // Subsistence
      emp_wages: "38500", // Total wages
      emp_gross: "38500",

      // Deductions
      emp_epf: "2400",
      emp_esi: "720",
      emp_pt: "200",
      emp_it: "0", // Income Tax
      emp_ins: "0", // Insurance
      emp_sal_adv: "0", // Salary Advance
      emp_l_cash: "0", // Loan/Cash
      emp_fines: "0",
      emp_dmg: "0", // Damages
      emp_d_sus: "0", // Suspension
      emp_soc: "0", // Society/Other
      mat_benefit: "0", // Maternity Benefit
      others: "0", // Other deductions
      tot_ded: "3320",

      // Net Pay
      net_pay: "35180",
      pay_mode: "Bank Transfer"
    },
    {
      slno: "2",
      emp_id: "EMP002",
      emp_name: "Anita",
      emp_lname: "Sharma",
      emp_gender: "Female",
      emp_desig: "Quality Inspector",
      emp_doj: "2024-08-01",
      emp_uan: "100234567890",
      emp_esino: "2345678901",

      emp_pay_days: "26",
      emp_ot_hrs: "4",
      nfh: "4",

      emp_basic: "24000",
      emp_da: "3600",
      emp_hra: "9600",
      emp_conv: "1920",
      emp_m_allow: "1200",
      emp_s_allow: "1000",
      emp_other: "800",
      emp_ot: "1000",
      emp_bonus: "1200",
      emp_subsis: "500",
      emp_wages: "44820",
      emp_gross: "44820",

      emp_epf: "2880",
      emp_esi: "840",
      emp_pt: "200",
      emp_it: "0",
      emp_ins: "0",
      emp_sal_adv: "0",
      emp_l_cash: "0",
      emp_fines: "0",
      emp_dmg: "0",
      emp_d_sus: "0",
      emp_soc: "0",
      mat_benefit: "0",
      others: "0",
      tot_ded: "3920",

      net_pay: "40900",
      pay_mode: "Bank Transfer"
    }
  ]
};

const FormTMusterRoll = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Fetch data from API on component mount
    const fetchFormData = async () => {
      try {
        console.log('Fetching Form T data from API...');
        // const response = await fetch('/api/form-t-data');
        // const data = await response.json();
        // setFormData(data);
        console.log('Using mock data for Form T');
      } catch (error) {
        console.error('Error fetching Form T data:', error);
        console.log('Falling back to mock data');
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = {
      ...updatedEmployees[index],
      [field]: value
    };

    // Auto-calculate totals if earnings or deductions change
    if (field.startsWith('emp_') || field === 'mat_benefit' || field === 'others') {
      const emp = updatedEmployees[index];

      // Calculate gross/total wages
      const basic = parseFloat(emp.emp_basic) || 0;
      const da = parseFloat(emp.emp_da) || 0;
      const hra = parseFloat(emp.emp_hra) || 0;
      const conv = parseFloat(emp.emp_conv) || 0;
      const mAllow = parseFloat(emp.emp_m_allow) || 0;
      const sAllow = parseFloat(emp.emp_s_allow) || 0;
      const other = parseFloat(emp.emp_other) || 0;
      const ot = parseFloat(emp.emp_ot) || 0;
      const bonus = parseFloat(emp.emp_bonus) || 0;
      const subsis = parseFloat(emp.emp_subsis) || 0;

      const gross = basic + da + hra + conv + mAllow + sAllow + other + ot + bonus + subsis;
      emp.emp_gross = gross.toString();
      emp.emp_wages = gross.toString();

      // Calculate total deductions
      const epf = parseFloat(emp.emp_epf) || 0;
      const esi = parseFloat(emp.emp_esi) || 0;
      const pt = parseFloat(emp.emp_pt) || 0;
      const it = parseFloat(emp.emp_it) || 0;
      const ins = parseFloat(emp.emp_ins) || 0;
      const salAdv = parseFloat(emp.emp_sal_adv) || 0;
      const lCash = parseFloat(emp.emp_l_cash) || 0;
      const fines = parseFloat(emp.emp_fines) || 0;
      const dmg = parseFloat(emp.emp_dmg) || 0;
      const dSus = parseFloat(emp.emp_d_sus) || 0;
      const soc = parseFloat(emp.emp_soc) || 0;
      const matBenefit = parseFloat(emp.mat_benefit) || 0;
      const others = parseFloat(emp.others) || 0;

      const totDed = epf + esi + pt + it + ins + salAdv + lCash + fines + dmg + dSus + soc + matBenefit + others;
      emp.tot_ded = totDed.toString();

      // Calculate net pay
      const netPay = gross - totDed;
      emp.net_pay = netPay.toString();
    }

    setFormData(prev => ({
      ...prev,
      employees: updatedEmployees
    }));
  };

  const addEmployee = () => {
    const newEmployee = {
      slno: (formData.employees.length + 1).toString(),
      emp_id: "",
      emp_name: "",
      emp_lname: "",
      emp_gender: "Male",
      emp_desig: "",
      emp_doj: "",
      emp_uan: "",
      emp_esino: "",
      emp_pay_days: "",
      emp_ot_hrs: "",
      nfh: "",
      emp_basic: "",
      emp_da: "",
      emp_hra: "",
      emp_conv: "",
      emp_m_allow: "",
      emp_s_allow: "",
      emp_other: "",
      emp_ot: "",
      emp_bonus: "",
      emp_subsis: "",
      emp_wages: "0",
      emp_gross: "0",
      emp_epf: "",
      emp_esi: "",
      emp_pt: "",
      emp_it: "0",
      emp_ins: "0",
      emp_sal_adv: "0",
      emp_l_cash: "0",
      emp_fines: "0",
      emp_dmg: "0",
      emp_d_sus: "0",
      emp_soc: "0",
      mat_benefit: "0",
      others: "0",
      tot_ded: "0",
      net_pay: "0",
      pay_mode: "Bank Transfer"
    };
    setFormData(prev => ({
      ...prev,
      employees: [...prev.employees, newEmployee]
    }));
  };

  const removeEmployee = (index) => {
    if (formData.employees.length > 1) {
      const updatedEmployees = formData.employees.filter((_, idx) => idx !== index);
      setFormData(prev => ({ ...prev, employees: updatedEmployees }));
      // If we're on a step beyond the new employee count, go back
      if (currentStep > updatedEmployees.length) {
        setCurrentStep(updatedEmployees.length);
      }
    }
  };

  const nextStep = () => {
    const totalSteps = 1 + formData.employees.length; // 1 (organization) + employees
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('Starting Form T (Muster Roll) Excel generation...');
      console.log('Form Data:', formData);

      // Load the template
      const response = await fetch('/templates/Form-T.xlsx');
      const arrayBuffer = await response.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      console.log('Template loaded successfully');

      // Remove ALL background fills from the entire worksheet
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
          }
        });
      });

      console.log('Background fills removed');

      // Two-pass placeholder replacement strategy

      // Pass 1: Replace form-level fields (organization, contractor, dates)
      console.log('Pass 1: Replacing form-level fields...');
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check form-level fields
              if (formData[normalizedFieldName] !== undefined) {
                console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${formData[normalizedFieldName]}"`);
                newValue = newValue.replace(match[0], formData[normalizedFieldName]);
              }
            }

            if (newValue !== cellValue) {
              cell.value = newValue;
            }
          }
        });
      });

      // Pass 2: Replace employee-specific fields for the first employee
      // (Form T typically shows one employee per row, but template has placeholders for first employee)
      console.log('Pass 2: Replacing employee fields...');
      if (formData.employees && formData.employees.length > 0) {
        const employee = formData.employees[0];

        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value ? String(cell.value) : '';

            if (cellValue.includes('{{')) {
              const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
              let newValue = cellValue;
              let match;

              while ((match = placeholderRegex.exec(cellValue)) !== null) {
                const fieldName = match[1].trim();
                const normalizedFieldName = fieldName.replace(/\s+/g, '_');

                // Check employee fields - including special fields like slno, nfh, mat_benefit, others, etc.
                if (employee[normalizedFieldName] !== undefined) {
                  console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${employee[normalizedFieldName]}"`);
                  newValue = newValue.replace(match[0], employee[normalizedFieldName]);
                }
              }

              if (newValue !== cellValue) {
                cell.value = newValue;
              }
            }
          });
        });
      }

      console.log('All placeholders replaced');

      // Generate Form file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-T-Muster-Roll-${formData.cur_month}-${formData.cur_year}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Excel file generated and downloaded successfully');

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form T - Muster Roll cum Wage Register Excel generated successfully!');
      }, 500);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(`Error generating Excel: ${error.message}`);
      setIsGenerating(false);
    }
  };

  // Calculate total steps: 1 (organization) + employees
  const totalSteps = 1 + formData.employees.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Render step content
  const renderStepContent = () => {
    // Step 0: Organization, Contractor & Period
    if (currentStep === 0) {
      return (
        <>
          <div className="form-section">
            <div className="section-header">
              <Building2 size={20} />
              <h3>Organization Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Organization Name</label>
                <input
                  type="text"
                  value={formData.est_name}
                  onChange={(e) => handleInputChange('est_name', e.target.value)}
                />
              </div>
              <div className="form-field full-width">
                <label>Organization Address</label>
                <textarea
                  value={formData.est_address}
                  onChange={(e) => handleInputChange('est_address', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Briefcase size={20} />
              <h3>Contractor Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Contractor Name</label>
                <input
                  type="text"
                  value={formData.contractor_name}
                  onChange={(e) => handleInputChange('contractor_name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Contractor Address</label>
                <input
                  type="text"
                  value={formData.contractor_address}
                  onChange={(e) => handleInputChange('contractor_address', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Calendar size={20} />
              <h3>Period Details</h3>
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Month</label>
                <select
                  value={formData.cur_month}
                  onChange={(e) => handleInputChange('cur_month', e.target.value)}
                >
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Year</label>
                <input
                  type="number"
                  value={formData.cur_year}
                  onChange={(e) => handleInputChange('cur_year', e.target.value)}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    // Steps 1 to N: Employee details
    if (currentStep > 0 && currentStep <= formData.employees.length) {
      const employeeIndex = currentStep - 1;
      const employee = formData.employees[employeeIndex];

      return (
        <div className="form-section" style={{ background: 'var(--theme-surface)', border: '1px solid #e5e7eb' }}>
          <div className="section-header" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={20} />
              <h3>Employee {employeeIndex + 1} - Wage Details</h3>
            </div>
            {formData.employees.length > 1 && (
              <button
                onClick={() => removeEmployee(employeeIndex)}
                style={{
                  background: '#ef4444',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Trash2 size={16} />
                Remove Employee
              </button>
            )}
          </div>

          {/* Basic Info */}
          <h4 style={{ margin: '1rem 0 0.5rem 0', fontSize: '0.95rem', color: '#374151' }}>Basic Information</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Sl. No</label>
              <input type="text" value={employee.slno} onChange={(e) => handleEmployeeChange(employeeIndex, 'slno', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Employee ID</label>
              <input type="text" value={employee.emp_id} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_id', e.target.value)} />
            </div>
            <div className="form-field">
              <label>First Name</label>
              <input type="text" value={employee.emp_name} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_name', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Last Name</label>
              <input type="text" value={employee.emp_lname} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_lname', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Gender</label>
              <select value={employee.emp_gender} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_gender', e.target.value)}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-field">
              <label>Designation</label>
              <input type="text" value={employee.emp_desig} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_desig', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Date of Joining</label>
              <DateInput value={employee.emp_doj} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_doj', e.target.value)} />
            </div>
            <div className="form-field">
              <label>UAN Number</label>
              <input type="text" value={employee.emp_uan} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_uan', e.target.value)} />
            </div>
            <div className="form-field">
              <label>ESIC Number</label>
              <input type="text" value={employee.emp_esino} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_esino', e.target.value)} />
            </div>
          </div>

          {/* Attendance */}
          <h4 style={{ margin: '1.5rem 0 0.5rem 0', fontSize: '0.95rem', color: '#374151' }}>Attendance</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Pay Days</label>
              <input type="number" value={employee.emp_pay_days} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_pay_days', e.target.value)} />
            </div>
            <div className="form-field">
              <label>OT Hours</label>
              <input type="number" value={employee.emp_ot_hrs} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_ot_hrs', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Holidays (NFH)</label>
              <input type="number" value={employee.nfh} onChange={(e) => handleEmployeeChange(employeeIndex, 'nfh', e.target.value)} />
            </div>
          </div>

          {/* Earnings */}
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#e0f2fe', borderRadius: '6px' }}>
            <div className="section-header">
              <IndianRupee size={18} />
              <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Earnings</h4>
            </div>
            <div className="form-grid" style={{ marginTop: '0.75rem' }}>
              <div className="form-field">
                <label>Basic (₹)</label>
                <input type="number" value={employee.emp_basic} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_basic', e.target.value)} />
              </div>
              <div className="form-field">
                <label>DA (₹)</label>
                <input type="number" value={employee.emp_da} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_da', e.target.value)} />
              </div>
              <div className="form-field">
                <label>HRA (₹)</label>
                <input type="number" value={employee.emp_hra} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_hra', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Conveyance (₹)</label>
                <input type="number" value={employee.emp_conv} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_conv', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Medical Allow (₹)</label>
                <input type="number" value={employee.emp_m_allow} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_m_allow', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Special Allow (₹)</label>
                <input type="number" value={employee.emp_s_allow} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_s_allow', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Other Allow (₹)</label>
                <input type="number" value={employee.emp_other} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_other', e.target.value)} />
              </div>
              <div className="form-field">
                <label>OT Wages (₹)</label>
                <input type="number" value={employee.emp_ot} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_ot', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Bonus (₹)</label>
                <input type="number" value={employee.emp_bonus} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_bonus', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Subsistence (₹)</label>
                <input type="number" value={employee.emp_subsis} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_subsis', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Gross Salary (₹)</label>
                <input type="text" value={employee.emp_gross} disabled className="calculated-field" />
              </div>
            </div>
          </div>

          {/* Deductions */}
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#fee2e2', borderRadius: '6px' }}>
            <div className="section-header">
              <IndianRupee size={18} />
              <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Deductions</h4>
            </div>
            <div className="form-grid" style={{ marginTop: '0.75rem' }}>
              <div className="form-field">
                <label>EPF (₹)</label>
                <input type="number" value={employee.emp_epf} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_epf', e.target.value)} />
              </div>
              <div className="form-field">
                <label>ESI (₹)</label>
                <input type="number" value={employee.emp_esi} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_esi', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Prof. Tax (₹)</label>
                <input type="number" value={employee.emp_pt} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_pt', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Income Tax (₹)</label>
                <input type="number" value={employee.emp_it} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_it', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Insurance (₹)</label>
                <input type="number" value={employee.emp_ins} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_ins', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Salary Advance (₹)</label>
                <input type="number" value={employee.emp_sal_adv} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_sal_adv', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Loan/Cash (₹)</label>
                <input type="number" value={employee.emp_l_cash} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_l_cash', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Fines (₹)</label>
                <input type="number" value={employee.emp_fines} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_fines', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Damages (₹)</label>
                <input type="number" value={employee.emp_dmg} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dmg', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Suspension (₹)</label>
                <input type="number" value={employee.emp_d_sus} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_d_sus', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Society (₹)</label>
                <input type="number" value={employee.emp_soc} onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_soc', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Mat. Benefit (₹)</label>
                <input type="number" value={employee.mat_benefit} onChange={(e) => handleEmployeeChange(employeeIndex, 'mat_benefit', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Others (₹)</label>
                <input type="number" value={employee.others} onChange={(e) => handleEmployeeChange(employeeIndex, 'others', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Total Deductions (₹)</label>
                <input type="text" value={employee.tot_ded} disabled className="calculated-field" />
              </div>
            </div>
          </div>

          {/* Net Pay */}
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#d1fae5', borderRadius: '6px' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#065f46' }}>Net Pay</h4>
            <div className="form-grid">
              <div className="form-field">
                <label style={{ fontSize: '1rem', fontWeight: '600' }}>Net Pay (₹)</label>
                <input type="text" value={employee.net_pay} disabled style={{ fontSize: '1.1rem', fontWeight: 'bold' }} className="calculated-field" />
              </div>
              <div className="form-field">
                <label>Payment Mode</label>
                <select value={employee.pay_mode} onChange={(e) => handleEmployeeChange(employeeIndex, 'pay_mode', e.target.value)}>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
            </div>
          </div>

          {/* Add Another Employee Button */}
          <button
            onClick={addEmployee}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#10b981',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginTop: '1.5rem'
            }}
          >
            <Plus size={20} />
            Add Another Employee
          </button>
        </div>
      );
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form T - Muster Roll cum Wage Register</h2>
          <p className="appointment-subtitle">Comprehensive Attendance and Wage Register [Rule 78(2)(b)]</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {currentStep === 0 ? 'Organization & Period Info' : `Employee ${currentStep}`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: currentStep === 0 ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
              color: currentStep === 0 ? '#9ca3af' : '#374151',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentStep < totalSteps - 1 ? (
            <button
              onClick={nextStep}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                color: 'var(--theme-surface, #ffffff)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          ) : (
            <button
              onClick={generateExcel}
              disabled={isGenerating}
              className={`btn-generate ${isGenerating ? 'generating' : ''}`}
            >
              {isGenerating ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                  </svg>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <FileDown size={20} />
                  <span>Generate Form</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormTMusterRoll;
